import { WarnIcon } from "@assets";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  FormControl,
  FormLabel,
  Icon,
  Switch,
} from "@chakra-ui/react";
import Dialog from "@components/Dialog/Dialog";
import DynamicForm from "@components/DynamicForm/DynamicForm";
import { UIFormField } from "@components/DynamicForm/types";
import StarIcon from "@icons/StarIcon";
import TicketIcon from "@icons/TicketMonoIcon";
import { EventType, NFTEvent, NFTEventTier } from "@models/event";
import { FormFieldType } from "@models/form";
import { Network } from "@models/network";
import { TransactionItem } from "@models/payment";
import { User } from "@models/user";
import { compareText } from "@models/utils";
import { useUserStore } from "@providers/auth";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { BiInfoCircle } from "react-icons/bi";
import { MdError } from "react-icons/md";
import { useDebounce } from "usehooks-ts";
import { usePaymentStore } from "../../../payment";

const USER_APP_HOST = process.env.NEXT_PUBLIC_USER_APP_HOST || "";
const DEBOUNCE_MS = 500;

const t = (key: string) => key;

const returnIconOfEventType = (eventType: EventType) => {
  switch (eventType) {
    case EventType.TICKET:
      return TicketIcon;
    default:
    case EventType.BENEFIT:
      return StarIcon;
  }
};

const returnNameOfEventType = (eventType: EventType) => {
  switch (eventType) {
    case EventType.TICKET:
      return t("Ticket");
    case EventType.MEMBERSHIP:
      return t("Membership Details");
    default:
    case EventType.BENEFIT:
      return t("Details");
  }
};

const toDefaultContactFields = (
  isBuyForOthers: boolean,
  isWhitelisted: boolean,
  disableBuyForOthers: boolean
): UIFormField[] => {
  return [
    {
      type: FormFieldType.EMAIL,
      label: {
        en: `${isBuyForOthers ? "Recipient " : ""}Email Address`,
      },
      key: "email",
      required: true,
      confirm: true,
      readonly: isWhitelisted || (disableBuyForOthers && !isBuyForOthers),
    },
    {
      type: FormFieldType.TEXT,
      label: {
        en: `${isBuyForOthers ? "Recipient " : ""}Name`,
      },
      key: "name",
      required: true,
    },
  ];
};

const toContactFormWalletField = (
  user: User,
  tier: NFTEventTier,
  whitelist?: string[]
): UIFormField[] => {
  try {
    let wallets = user.getWalletAddressesByNetwork(
      tier.airdropConfig?.network || Network.EVM
    );

    if (whitelist && !isEmpty(whitelist)) {
      const validWallets = wallets.filter((w) =>
        whitelist.includes(w.toLowerCase().trim())
      );
      if (validWallets.length > 0) wallets = validWallets;
    }

    const options = wallets.map((address) => ({
      label: {},
      value: address,
    }));
    return [
      {
        type: FormFieldType.SELECT,
        label: {
          en: "Receiving Wallet",
        },
        key: "wallet",
        required: true,
        options,
        span: 2,
        tooltip: {
          en: "Choose the wallet you would like to receive the NFT. For first-time user, we have created a non-custodial wallet for you.",
        },
        note: {
          en: `<a href='${USER_APP_HOST}/profile' target='_blank'>Want to receive in another wallet? <span class='underline text-primary-200'>Connect in profile page</span> and then refresh.</a>`,
        },
      },
    ];
  } catch (error) {}
  return [];
};

const TransactionItemContact = ({
  user,
  event,
  item,
  idx,
  onChange: onChange = () => {},
  onValidChange: onValidChange = () => {},
}: {
  user: User | null;
  event: NFTEvent;
  item: TransactionItem;
  idx: number;
  onChange?: (idx: number, values: FieldValues) => void;
  onValidChange?: (idx: number, valid: boolean) => void;
}) => {
  const tier: NFTEventTier | null = useMemo(() => {
    if (!event || !item.productId) return null;
    return event.tiers.find((t) => t.id == item.productId) || null;
  }, [event, item?.productId]);
  const discount = usePaymentStore((state) => state.discount);

  const [isBuyForOthers, setIsBuyForOthers] = useState(idx != 0);
  const [initialized, setInitialized] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isDirty },
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm();

  const watchEmail = watch("email");
  const debouncedBuyForOtherEmail = useDebounce<string>(
    watchEmail,
    DEBOUNCE_MS
  );
  const availableQuantity = useMemo(() => {
    if (!tier?.paymentConfig) return 0;
    if (tier.paymentConfig.max < 0) return 99999;

    const discountRemainUse =
      discount && discount.max > 0 ? discount.remainUse : -1;

    const remainQuantity = Math.max(
      tier.paymentConfig.max - tier.paymentConfig.sold,
      0
    );
    return discountRemainUse > 0
      ? Math.min(discountRemainUse, remainQuantity)
      : remainQuantity;
  }, [tier?.paymentConfig?.max, tier?.paymentConfig?.sold, discount?.id]);

  useEffect(() => {
    if (!user?.email) return;
    if (idx === 0) {
      if (compareText(user.email, debouncedBuyForOtherEmail)) {
        setIsBuyForOthers(false);
      } else if (debouncedBuyForOtherEmail) {
        setIsBuyForOthers(true);
      }
    }
  }, [debouncedBuyForOtherEmail, user?.email]);

  useEffect(() => {
    const subscription = watch(async () => {
      trigger();
      onChange(idx, getValues());
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    //console.log("isValid", isValid);
    onValidChange(idx, isValid);
  }, [isValid]);

  useEffect(() => {
    if (!initialized) return;
    const user = useUserStore.getState().user;
    if (!user) return;

    const email = isBuyForOthers
      ? compareText(item.contact.email, user.email)
        ? ""
        : item.contact.email
      : user.email || "";
    setValue("email", email);
    setValue("_email", email);

    if (isBuyForOthers) {
      setValue("wallet", "");
      setValue("name", "");
    } else {
      setValue(
        "wallet",
        user.getWalletAddressesByNetwork(
          tier?.airdropConfig?.network || Network.EVM
        )[0]
      );
    }
  }, [isBuyForOthers, initialized]);

  useEffect(() => {
    if (!tier) return;
    console.log("Tier", tier);
    if (tier.paymentConfig?.disableBuyForOthers) setIsBuyForOthers(false);
  }, [tier, initialized]);

  // Prefill contact info if available
  useEffect(() => {
    if (item.contact.wallet) {
      setValue("wallet", item.contact.wallet);
    }
    if (item.contact.email) {
      if (idx == 0)
        setIsBuyForOthers(!compareText(item.contact.email, user?.email));
      setValue("email", item.contact.email);
      setValue("_email", item.contact.email);
    }
    if (item.contact.name) setValue("name", item.contact.name);

    for (const info of item.extraInfo || []) {
      if (info.value) setValue(info.key, info.value);
    }
    setInitialized(true);
  }, []);

  const _t = (key: string) => key;

  const fields = useMemo(() => {
    const customForm = tier?.paymentConfig?.customForm || [];
    const customFormKeys = customForm.map((field) => field.key);
    const defaultForm = toDefaultContactFields(
      isBuyForOthers,
      !!tier?.paymentConfig?.isWhitelisted && idx == 0,
      !!tier?.paymentConfig?.disableBuyForOthers
    ).filter((field) => !customFormKeys.includes(field.key));

    const bulkQuantityField = {
      type: FormFieldType.NUMBER,
      label: {
        en: "Purchase Quantity",
      },
      key: "bulkQuantity",
      span: 2,
      min: 1,
      required: true,
      value: item.quantity,
      ...(tier?.paymentConfig?.max && tier.paymentConfig.max > 0
        ? { max: availableQuantity }
        : {}),
    };
    console.log(availableQuantity, bulkQuantityField);

    return [
      ...(event.isERC1155BulkPurchase ? [bulkQuantityField] : []),
      ...defaultForm,
      ...customForm,
      ...(user && !isBuyForOthers && tier?.airdropConfig
        ? toContactFormWalletField(user, tier, tier?.paymentConfig?.whitelist)
        : []),
    ];
  }, [user, tier, isBuyForOthers, item.quantity, availableQuantity]);

  const BuyForOtherToggle = () => {
    return (
      <FormControl
        display="flex"
        flexDirection="row"
        alignItems="center"
        w="auto"
        mx="4"
        zIndex={10}
      >
        <FormLabel
          fontSize={{ base: "sm", sm: "lg" }}
          htmlFor="buy-for-others"
          my="0"
          mx="2"
        >
          {_t("Buy for others")}
        </FormLabel>
        <Switch
          id="buy-for-others"
          colorScheme="primary"
          size="md"
          isChecked={isBuyForOthers}
          onChange={(e) => {
            setIsBuyForOthers(!isBuyForOthers);
          }}
        />
      </FormControl>
    );
  };

  if (!item || !event || !tier?.paymentConfig) return <></>;

  return (
    <div className="border-t first:border-t-0">
      <AccordionItem
        _first={{ borderTop: "none" }}
        _last={{ borderBottom: "none" }}
        bg="transparent"
      >
        <h2 className="border-b border-neutral-60 dark:text-white dark:border-white/[.16]">
          <AccordionButton bg="transparent">
            <div className="flex flex-row flex-grow py-4 items-center gap-3">
              <Icon
                as={returnIconOfEventType(event.type)}
                w={5}
                h={5}
                color={"primary.300"}
              ></Icon>
              <h2 className="flex flex-row items-center flex-grow text-md sm:text-lg font-semibold dark:text-white">
                {event.type !== EventType.MEMBERSHIP
                  ? `${returnNameOfEventType(event.type)} ${idx + 1}`
                  : returnNameOfEventType(event.type)}{" "}
                {!isValid && (isDirty || idx > 0) && (
                  <MdError className="mx-4 text-error" />
                )}
              </h2>
              {!tier.paymentConfig.disableBuyForOthers && idx == 0 && (
                <BuyForOtherToggle />
              )}
            </div>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel
          style={{ backgroundColor: "transparent", padding: "1rem" }}
          p={4}
        >
          {isBuyForOthers && (
            <div className="flex flex-row dark:text-secondary sm:px-3 py-2 text-sm">
              <div className="mr-2 flex-shrink-0 line-height-1 mt-[4px]">
                <BiInfoCircle />
              </div>
              <p className="flex-grow">
                {_t(
                  "If you are buying tickets on behalf of someone else, the recipient will receive a redeem code for their NFT ticket via the email address that you provide below."
                )}
              </p>
            </div>
          )}
          <DynamicForm
            fields={fields}
            errors={errors}
            register={register}
            handleSubmit={handleSubmit}
            isDirty={isDirty}
            setValue={setValue}
          />
        </AccordionPanel>
      </AccordionItem>
      <Dialog
        isOpen={showWarning}
        title={""}
        actions={[{ text: "Dismiss", onClick: () => setShowWarning(false) }]}
        icon={WarnIcon}
      >
        <p className="w-full text-center">
          {_t("You can only buy 1 ticket for yourself.")}
        </p>
      </Dialog>
    </div>
  );
};

const CheckoutContactForm = ({
  onChange: onChange = () => {},
  onValidChange: onValidChange = () => {},
}: {
  onChange: (idx: number, values: FieldValues) => void;
  onValidChange: (idx: number, valid: boolean) => void;
}) => {
  const user = useUserStore((state) => state.user);
  const transaction = usePaymentStore((state) => state.transaction);
  const event = usePaymentStore((state) => state.event);

  if (!user || !transaction || !event) return <></>;

  return (
    <div>
      <Accordion
        allowMultiple={true}
        defaultIndex={[0]}
        reduceMotion={true}
        className="rounded-xl overflow-hidden border border-neutral-60 dark:border-white/[.16] bg-white/[.6] dark:bg-white/[.05]"
      >
        {transaction.items.map((item, i) => {
          return (
            <TransactionItemContact
              key={i}
              user={user}
              event={event}
              item={item}
              idx={i}
              onChange={onChange}
              onValidChange={onValidChange}
            />
          );
        })}
      </Accordion>
    </div>
  );
};

export default CheckoutContactForm;
